// src/registry/ComponentRegistry.js
import loadable from '../utils/loadable';

/**
 * Registry for managing client-specific component overrides.
 * Allows different clients to have custom component implementations
 * while maintaining a consistent interface.
 */
class ComponentRegistry {
  constructor() {
    // Map of component keys to client-specific implementations
    this.components = new Map();
  }

  /**
   * Register a client-specific component implementation
   * @param {string} clientId - Client identifier
   * @param {string} key - Component identifier
   * @param {React.ComponentType} component - Client-specific component implementation
   * @returns {ComponentRegistry} - Registry instance for chaining
   */
  register(key, component) {
    this.components.set(key, component);
    return this;
  }

  /**
   * Register a lazily-loaded client-specific component
   * @param {string} clientId - Client identifier
   * @param {string} key - Component identifier
   * @param {Function} importFunc - Import function for lazy loading
   * @param {React.ReactNode} fallback - Fallback UI during loading
   * @returns {ComponentRegistry} - Registry instance for chaining
   */
  registerLazy(key, importFunc, fallback = null) {
    const lazyComponent = loadable(importFunc, { fallback });
    return this.register(key, lazyComponent);
  }

  /**
   * Get the appropriate component implementation for a client
   * @param {string} key - Component identifier
   * @param {string} clientId - Client identifier
   * @returns {React.ComponentType|null} - Component implementation or null if not found
   */
  get(key) {
    return this.components.get(key);
  }

  /**
   * Remove all registered components
   * @returns {ComponentRegistry} - Registry instance for chaining
   */
  clear() {
    this.components.clear();
    return this;
  }
}

// Create and export singleton instance
export const componentRegistry = new ComponentRegistry();
